import React, { useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import BackOfficeNavbar from './components/BackOfficeNavbar';
import { isPathActive, PageTabs } from './navigation';
import backOfficeRoutes from 'routesBackOffice';
import axios from 'axios';
import { CONFIG } from 'variables/config';

const ADMIN_PIN_KEY = 'backoffice_pin';

let sessionPin: string | null = localStorage.getItem(ADMIN_PIN_KEY);

// Module-level interceptor so it's active before any child component renders.
axios.interceptors.request.use((config) => {
    if (sessionPin) config.headers['x-admin-pin'] = sessionPin;
    return config;
});

export default function BackOfficeLayout() {
    const navigate = useNavigate();
    const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
    const [pinVerified, setPinVerified] = useState(() => sessionStorage.getItem('backoffice_pin_verified') === 'true');
    const [pinInput, setPinInput] = useState('');
    const [pinError, setPinError] = useState('');
    const [pinLoading, setPinLoading] = useState(false);
    const [pinAttempts, setPinAttempts] = useState(0);
    const [currentRoute, setCurrentRoute] = useState('Back Office');
    const pinInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const verifyAdmin = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/auth/sign-in');
                return;
            }

            try {
                const response = await axios.get(`${CONFIG.apiUrl}/user/me`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                const user = response.data;
                if (user.role !== 'admin') {
                    navigate('/admin');
                    return;
                }

                setIsAdmin(true);

                const storedPin = localStorage.getItem(ADMIN_PIN_KEY);
                if (storedPin) {
                    try {
                        await axios.post(`${CONFIG.apiUrl}/auth/verify-admin-pin`, {}, {
                            headers: { Authorization: `Bearer ${token}` },
                        });
                        sessionStorage.setItem('backoffice_pin_verified', 'true');
                        setPinVerified(true);
                    } catch {
                        localStorage.removeItem(ADMIN_PIN_KEY);
                        sessionPin = null;
                    }
                }
            } catch (error) {
                console.error('Admin verification failed:', error);
                navigate('/auth/sign-in');
            }
        };

        verifyAdmin();
    }, [navigate]);

    useEffect(() => {
        if (isAdmin && !pinVerified) {
            setTimeout(() => pinInputRef.current?.focus(), 50);
        }
    }, [isAdmin, pinVerified]);

    const handlePinSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (pinAttempts >= 3 || pinLoading) return;

        setPinLoading(true);
        setPinError('');

        // Set in memory so the interceptor can attach it to the validation request.
        sessionPin = pinInput;

        try {
            const token = localStorage.getItem('token');
            await axios.post(`${CONFIG.apiUrl}/auth/verify-admin-pin`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            localStorage.setItem(ADMIN_PIN_KEY, pinInput);
            sessionStorage.setItem('backoffice_pin_verified', 'true');
            setPinVerified(true);
        } catch (err: any) {
            sessionPin = null;
            localStorage.removeItem(ADMIN_PIN_KEY);
            const next = pinAttempts + 1;
            setPinAttempts(next);
            setPinInput('');
            if (next >= 3) {
                setPinError('Too many wrong attempts. Refresh the page to try again.');
            } else {
                setPinError(`Wrong PIN. ${3 - next} attempt${3 - next === 1 ? '' : 's'} remaining.`);
            }
        } finally {
            setPinLoading(false);
        }
    };

    const location = useLocation();

    useEffect(() => {
        const getActiveRoute = () => {
            for (let i = 0; i < backOfficeRoutes.length; i++) {
                const route = backOfficeRoutes[i];
                if (isPathActive(location.pathname, route)) {
                    setCurrentRoute(route.name);
                    break;
                }
            }
        };
        getActiveRoute();
    }, [location.pathname]);

    const getRoutes = (routes: RoutesType[]): any => {
        return routes.map((prop, key) => {
            if (prop.layout === '/back-office') {
                return (
                    <Route
                        path={`${prop.path}`}
                        element={prop.tabGroup ? <PageTabs tabGroup={prop.tabGroup}>{prop.component}</PageTabs> : prop.component}
                        key={key}
                    />
                );
            }
            return null;
        });
    };

    if (isAdmin === null) {
        return (
            <div className="flex h-screen w-screen items-center justify-center">
                <div>Loading...</div>
            </div>
        );
    }

    return (
        <div className="flex h-full w-full flex-col">
            {isAdmin && !pinVerified && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="w-full max-w-sm rounded-2xl bg-white p-8 shadow-2xl dark:bg-navy-800">
                        <h2 className="mb-1 text-center text-xl font-bold text-navy-700 dark:text-white">
                            Back Office
                        </h2>
                        <p className="mb-6 text-center text-sm text-gray-500">
                            Enter your 4-digit PIN to continue
                        </p>
                        <form onSubmit={handlePinSubmit}>
                            <input
                                ref={pinInputRef}
                                type="password"
                                inputMode="numeric"
                                maxLength={4}
                                value={pinInput}
                                onChange={(e) => setPinInput(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                disabled={pinAttempts >= 3 || pinLoading}
                                placeholder="••••"
                                className="mb-3 w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-center text-2xl tracking-[0.5em] outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                            />
                            {pinError && (
                                <p className="mb-3 text-center text-sm text-red-500">{pinError}</p>
                            )}
                            <button
                                type="submit"
                                disabled={pinInput.length !== 4 || pinAttempts >= 3 || pinLoading}
                                className="w-full rounded-xl bg-brand-500 py-3 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:opacity-40"
                            >
                                {pinLoading ? 'Verifying...' : 'Unlock'}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            <BackOfficeNavbar currentRoute={currentRoute} />

            <div className="h-full w-full overflow-auto bg-lightPrimary dark:bg-navy-900">
                <div className="mx-auto min-h-screen p-5 !pt-[68px] md:p-5">
                    {pinVerified && (
                        <Routes>
                            {getRoutes(backOfficeRoutes)}
                            <Route
                                path="/"
                                element={<Navigate to="/back-office/dashboard" replace />}
                            />
                        </Routes>
                    )}
                </div>
            </div>
        </div>
    );
}
